import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { FaCircleNotch } from 'react-icons/fa'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import {
    validAreaCode,
    notValidAreaCode,
} from '../store/actions/areaCodeAction'
import {
    registeredEmail,
    notRegisteredEmail,
} from '../store/actions/emailAction'
import {
    BusinessIdValidation,
    PostalCodeValidation,
    EmailValidation,
    ContentfulPersonLocationPostalCodeFieldName,
    ContentfulPersonEmailFieldName,
    ContentfulBusinessIdFieldName,
    ContentfulBusinessNameFieldName,
    ContentfulBusinessPhoneNumberFieldName,
    ContentfulBusinessContactPersonEmailFieldName,
    ContentfulBusinessLocationPostalCodeFieldName,
    ContentfulBusinessLocationCityFieldName,
    ContentfulBusinessLocationAddressFieldName,
    ContentfulBusinessAdditionalInformationFieldName,
    MAX_CHARACTER_COUNT,
    personCustomerType,
    businessCustomerType,
} from '../constants/GeneralConstants'

import {
    getBusinessInformation,
    checkValidPostalcodeArea,
    checkCustomerEmailExists,
} from '../services/IntegrationService'

import checkmark from '../images/checkmark.png'
import { Field } from 'formik'

const Circle = styled(FaCircleNotch)`
    animation: spin 2s infinite linear;
    font-size: 3em;
    flex: 1;
    color: ${props => props.theme.colors.lightGreenText};

    @-moz-keyframes spin {
        100% {
            -moz-transform: rotate(360deg);
        }
    }
    @-webkit-keyframes spin {
        100% {
            -webkit-transform: rotate(360deg);
        }
    }
    @keyframes spin {
        100% {
            -webkit-transform: rotate(360deg);
            transform: rotate(360deg);
        }
    }
`

const Checkmark = styled.img`
    width: 15px;
    display: inline-block;
    margin-left: 0.5em;
`

const TextInputComponent = styled(Field)`
    width: 95%;
    border: 1px solid ${props => props.theme.colors.inputLightGrey};
    font-size: 15px;
    font-family: "Roboto";
    min-height: 150px;

    @media (max-width: ${props => props.theme.responsive.small}) {
        width: 100%;
    }

    &:focus {
        outline: none;
        border-color: ${props => props.theme.colors.darkGreen};
    }

    &::placeholder {
        opacity: 1;
        color: ${props => props.theme.colors.inputLightGreyText};
        padding-left: 10px;
        text-align: center;
    }
    &:-webkit-input-placeholder {
        opacity: 1;
        color: ${props => props.theme.colors.inputLightGreyText};
        padding-left: 10px;
    }
    &:-moz-placeholder {
        opacity: 1;
        color: ${props => props.theme.colors.inputLightGreyText};
        padding-left: 10px;
    }
    &:-ms-input-placeholder {
        opacity: 1;
        color: ${props => props.theme.colors.inputLightGreyText};
        padding-left: 10px;
    }
`

const CharacterCounter = styled.div`
    margin: 5px;
    width: 320px;
    &.warning {
        text-decoration: solid underline;
        text-decoration-color: red;
    }
`

const InputWithStateRegularInput = styled(Field)`
    width: 45%;
    border: none;
    border-bottom: 1px solid ${props => props.theme.colors.inputLightGrey};
    height: 25px;
    font-size: 15px;
    font-family: "Roboto";
    box-shadow: none;

    @media (max-width: ${props => props.theme.responsive.small}) {
        width: 100%;
    }

    &:invalid {
        border-color: red;
    }

    &:focus {
        outline: none;
        border-color: ${props => props.theme.colors.darkGreen};
    }

    &::placeholder {
        opacity: 1;
        color: ${props => props.theme.colors.inputLightGreyText};
        padding-left: 10px;
    }
    &:-webkit-input-placeholder {
        opacity: 1;
        color: ${props => props.theme.colors.inputLightGreyText};
        padding-left: 10px;
    }
    &:-moz-placeholder {
        opacity: 1;
        color: ${props => props.theme.colors.inputLightGreyText};
        padding-left: 10px;
    }
    &:-ms-input-placeholder {
        opacity: 1;
        color: ${props => props.theme.colors.inputLightGreyText};
        padding-left: 10px;
    }
`

const InputHeaderBusiness = styled.h3`
    @media (max-width: ${props => props.theme.responsive.small}) {
        font-size: 16px;
    }
    margin-bottom: 0.5em;
    font-weight: bold;
`

const InputHeaderPerson = styled.label`
    margin-bottom: 0.5em;
`

const InputErrorMessage = styled.p`
    color: red;
    font-size: 15px;
    font-weight: 700;
`

const ResponseMessage = styled.p`
    font-size: 15px;
    font-weight: 700;
`

const InputSpinner = styled(Circle)`
    width: 15px;
    height: 15px;
    margin-left: 8px;
`

const Wrapper = styled.div`
    padding-bottom: 3em;
`

class InputWithState extends React.Component {
    constructor(props) {
        super(props)

        this.state = {
            currentBusinessId: null,
            isPendingBusinessIdSearch: false,
            currentPersonPostalCode: null,
            isPendingPostCodeSearch: false,
            isValidPostalCode: true,
            isSuccessfulSearch: false,
            isPendingEmailSearch: false,
            isRegisteredEmail: false,
            currentEmail: null,
        }
    }

    componentDidUpdate(prevProps) {
        const {
            data,
            values,
            setFieldValue,
            setFieldTouched,
            checkPostalCode,
            siteMetadata,
            isAcceptedPostalCode,
            checkEmail,
            customerType,
        } = this.props

        const {
            integrationObjectName,
        } = data

        const isPostalCodeInputField = values &&
            values[ContentfulPersonLocationPostalCodeFieldName] &&
            integrationObjectName === ContentfulPersonLocationPostalCodeFieldName

        const isChangedPostalCode = this.props && this.props.values && this.props.values[ContentfulPersonLocationPostalCodeFieldName] &&
            this.props.values[ContentfulPersonLocationPostalCodeFieldName] !== prevProps.values[ContentfulPersonLocationPostalCodeFieldName] &&
            values[ContentfulPersonLocationPostalCodeFieldName].match(PostalCodeValidation) &&
            values[ContentfulPersonLocationPostalCodeFieldName] !== this.state.currentPersonPostalCode

        if(isPostalCodeInputField && isChangedPostalCode) {
            this.setState({
                isPendingPostCodeSearch: true,
                isSuccessfulSearch: false
            })
            checkPostalCode(values, siteMetadata).then( () => {
                if( isAcceptedPostalCode ) {
                    this.setState({
                        isPendingPostCodeSearch: false,
                        isSuccessfulSearch: true,
                        isValidPostalCode: true,
                    })
                } else {
                    this.setState({
                        isPendingPostCodeSearch: false,
                        isSuccessfulSearch: false,
                        isValidPostalCode: false,
                    })
                }
            })
        }

        const isPersonEmailInputField = values &&
            values[ContentfulPersonEmailFieldName] &&
            integrationObjectName === ContentfulPersonEmailFieldName

        const isChangedPersonEmail = this.props && this.props.values && this.props.values[ContentfulPersonEmailFieldName] &&
            this.props.values[ContentfulPersonEmailFieldName] !== prevProps.values[ContentfulPersonEmailFieldName] &&
            values[ContentfulPersonEmailFieldName].match(EmailValidation) &&
            values[ContentfulPersonEmailFieldName] !== this.state.currentPersonPostalCode

        const isBusinessEmailInputField = values &&
            values[ContentfulBusinessContactPersonEmailFieldName] &&
            integrationObjectName === ContentfulBusinessContactPersonEmailFieldName

        const isChangedBusinessEmail = this.props && this.props.values && this.props.values[ContentfulBusinessContactPersonEmailFieldName] &&
            this.props.values[ContentfulBusinessContactPersonEmailFieldName] !== prevProps.values[ContentfulBusinessContactPersonEmailFieldName] &&
            values[ContentfulBusinessContactPersonEmailFieldName].match(EmailValidation) &&
            values[ContentfulBusinessContactPersonEmailFieldName] !== this.state.currentEmail

        if((isPersonEmailInputField && isChangedPersonEmail) || (isBusinessEmailInputField && isChangedBusinessEmail ) ) {
            this.setState({
                isPendingEmailSearch: true,
                isSuccessfulSearch: false
            })
            const { frendsApiBaseurl, frendsApiKeyHeader } = siteMetadata
            checkEmail(values,
                customerType,
                frendsApiBaseurl,
                frendsApiKeyHeader
            ).then( () => {
                if( this.state.isRegisteredEmail ) {
                    this.setState({
                        isPendingEmailSearch: false,
                        isSuccessfulSearch: true,
                    })
                } else {
                    this.setState({
                        isPendingEmailSearch: false,
                        isSuccessfulSearch: false,
                    })
                }
            })
        }

        const isBusinessIdField = values &&
            values[ContentfulBusinessIdFieldName] &&
            integrationObjectName === ContentfulBusinessIdFieldName

        const isChangedBusinessId = this.props && this.props.values && this.props.values.businessId &&
            this.props.values.businessId !== prevProps.values.businessId &&
            values[ContentfulBusinessIdFieldName].match(BusinessIdValidation) &&
            values[ContentfulBusinessIdFieldName] !== this.state.currentBusinessId

        if(isBusinessIdField && isChangedBusinessId) {
         
          //TODO: Remove next 4 lines when Frends integration works!
         	this.setState({
                        isPendingBusinessIdSearch: false,
                        isSuccessfulSearch: true
                    })
          //TODO: Enable when Frends integration works!
          
          /* this.setState({
                isPendingBusinessIdSearch: true,
                isSuccessfulSearch: false
            })
            this.setInputFieldValue(values)
                .then( (result) => {
                    if(result.data.name) {
                        setFieldValue(ContentfulBusinessNameFieldName, result.data.name)
                        setFieldTouched(ContentfulBusinessNameFieldName, true)
                    }
                    if(result.data.phoneNumber) {
                        const phoneNumberWithoutSpaces = result.data.phoneNumber.replace(/\s+/g, '')
                        setFieldValue(ContentfulBusinessPhoneNumberFieldName, phoneNumberWithoutSpaces)
                        setFieldTouched(ContentfulBusinessPhoneNumberFieldName, true)
                    }
                    if(result.data.address) {
                        if(result.data.address.postCode) {
                            setFieldValue(ContentfulBusinessLocationPostalCodeFieldName, result.data.address.postCode)
                            setFieldTouched(ContentfulBusinessLocationPostalCodeFieldName, true)
                        }
                        if(result.data.address.city) {
                            setFieldValue(ContentfulBusinessLocationCityFieldName, result.data.address.city)
                            setFieldTouched(ContentfulBusinessLocationCityFieldName, true)
                        }
                        const address = this.parseAddress(result.data)
                        setFieldValue(ContentfulBusinessLocationAddressFieldName, address)
                        setFieldTouched(ContentfulBusinessLocationAddressFieldName, true)
                    }
                    this.setState({
                        isPendingBusinessIdSearch: false,
                        isSuccessfulSearch: true
                    })
                })*/
        }
    }

    parseAddress = (data) => {
        const { address: { street, building, stair, room } } = data
        const streetName = street || ''
        // eslint-disable-next-line no-extra-parens
        const streetNo = (building && (' ' + building)) || ''
        // eslint-disable-next-line no-extra-parens
        const staircase = (stair && (' ' + stair)) || ''
        // eslint-disable-next-line no-extra-parens
        const flat = (room && (' ' + room )) || ''
        const address = streetName + streetNo + staircase + flat
        return address
    }

    characterCounter(string){
        if(string) {
            return string.length
        }
        return 0
    }

    setInputFieldValue(values){
        const { siteMetadata } = this.props
        return new Promise(async (resolve, reject) => {
            try {
                const response = await getBusinessInformation(
                    values[ContentfulBusinessIdFieldName],
                    siteMetadata.frendsApiBaseurl,
                    siteMetadata.frendsApiKeyHeader
                )
                resolve(response)
            } catch (e) {
                this.setState({
                    isPendingBusinessIdSearch: false,
                    isSuccessfulSearch: false
                })
                reject(e)
            }
        })
    }

    render() {
        const {
            className,
            data,
            touched,
            errors,
            values,
            isAcceptedPostalCode,
            customerType,
            isEmailAlreadyRegistered,
        } = this.props

        const {
            header,
            required,
            description,
            placeholder,
            integrationObjectName,
            responseNotification,
            alreadyExistsMessage,
        } = data

        let characters = 0
        if ( values &&
            values[ContentfulBusinessAdditionalInformationFieldName]) {
            characters = this.characterCounter(values[ContentfulBusinessAdditionalInformationFieldName])
        }

        const isPerson = customerType === personCustomerType
        // RegularInput for input
        // TextInputComponent for text area input
        return (
            <Wrapper className={className}>
                {integrationObjectName === ContentfulBusinessAdditionalInformationFieldName
                    ?
                    <>
                {description && <>
                {description.description}
                </>}
                <TextInputComponent
                    name={integrationObjectName}
                    placeholder={placeholder}
                    component="textarea"
                />
                <CharacterCounter
                    className={characters > MAX_CHARACTER_COUNT && 'warning'}
                >
                    {`${characters} / ${MAX_CHARACTER_COUNT}`}
                </CharacterCounter>

                </>
                    :
                    <>
                    {isPerson
                        ?
                        <InputHeaderPerson>
                            {header} {required && required === true && '*'}
                        </InputHeaderPerson>
                        :
                        <InputHeaderBusiness>
                            {header} {required && required === true && '*'}
                        </InputHeaderBusiness>
                    }
                <InputWithStateRegularInput
                    className={isPerson
                        ? 'personInputWithStateRegularInput'
                        : ''}
                    name={integrationObjectName}
                    placeholder={placeholder}
                    component="input"
                />
                {(integrationObjectName === ContentfulPersonLocationPostalCodeFieldName || integrationObjectName === ContentfulBusinessIdFieldName) &&
                  (this.state.isPendingPostCodeSearch || this.state.isPendingBusinessIdSearch ) &&
                    <InputSpinner />
                }

                {integrationObjectName === ContentfulPersonEmailFieldName &&
                  (this.state.isPendingEmailSearch ) &&
                    <InputSpinner />
                }

                {integrationObjectName === ContentfulBusinessIdFieldName &&
                values[integrationObjectName] &&
                this.state.isSuccessfulSearch &&
                    <Checkmark src={checkmark} />
                }
                </>
                }
                {touched[integrationObjectName] &&
                 errors[integrationObjectName] &&
                <InputErrorMessage>
                    {errors[integrationObjectName]}
                </InputErrorMessage>}
                {touched[ContentfulPersonLocationPostalCodeFieldName] &&
                !isAcceptedPostalCode &&
                <ResponseMessage>
                    {responseNotification}
                </ResponseMessage>}

                {touched[ContentfulPersonEmailFieldName] &&
                isEmailAlreadyRegistered &&
                !errors[integrationObjectName] &&
                <ResponseMessage>
                    {alreadyExistsMessage}
                </ResponseMessage>}
                {touched[ContentfulBusinessContactPersonEmailFieldName] &&
                isEmailAlreadyRegistered &&
                !errors[integrationObjectName] &&
                <ResponseMessage>
                    {alreadyExistsMessage}
                </ResponseMessage>}

            </Wrapper>
        )
    }
}


const checkPostalCode = (values, siteMetadata) => {
    return function(dispatch) {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await checkValidPostalcodeArea (
                    values[ContentfulPersonLocationPostalCodeFieldName],
                    siteMetadata.frendsApiBaseurl,
                    siteMetadata.frendsApiKeyHeader
                )
                resolve(response)
            } catch (e) {
                reject(e)
            }
        }).then( result => {
            if(result.data) {
                if(result.data.success === true ) {
                    dispatch(validAreaCode())
                }
                if(result.data.success === false ) {
                    dispatch(notValidAreaCode())
                }
            }
        })
    }
}

const checkEmail = (
    values,
    customerType,
    frendsApiBaseurl,
    frendsApiKeyHeader,
) => {

    return function (dispatch) {
        return new Promise(async (resolve, reject) => {

            try {
                let email = ''
                if(customerType === personCustomerType) {
                    email = values[ContentfulPersonEmailFieldName]
                } else if (customerType === businessCustomerType ) {
                    email = values[ContentfulBusinessContactPersonEmailFieldName]
                }

                const response = await checkCustomerEmailExists (
                    email,
                    customerType,
                    frendsApiBaseurl,
                    frendsApiKeyHeader
                )
                resolve(response)
            } catch (error) {
                reject(error)
            }
        }).then( result => {
            if(result.data) {
                if(result.data.success == true) {
                    dispatch(registeredEmail())
                }
                if(result.data.success == false) {
                    dispatch(notRegisteredEmail())
                }
            }
        })
    }
}

const mapStateToProps = (state) => {
    return {
        isAcceptedPostalCode: state.areaCodeReducer.isAcceptedPostalCode,
        customerType: state.customerTypeReducer.customerType,
        isEmailAlreadyRegistered: state.emailReducer.isEmailAlreadyRegistered,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        checkPostalCode: bindActionCreators(checkPostalCode, dispatch),
        checkEmail: bindActionCreators(checkEmail, dispatch),
    }
}

InputWithState.propTypes = {
    className: PropTypes.string,
    values: PropTypes.object,
    data: PropTypes.object,
    touched: PropTypes.object,
    errors: PropTypes.object,
    errorMessages: PropTypes.object,
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
    queryData: PropTypes.object,
    siteMetadata: PropTypes.object,
    isAcceptedPostalCode: PropTypes.bool,
    checkPostalCode: PropTypes.func,
    checkEmail: PropTypes.func,
    isEmailAlreadyRegistered: PropTypes.bool,
    customerType: PropTypes.string,
}

export default connect(mapStateToProps, mapDispatchToProps)(InputWithState)
